import React, { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Toaster } from "../../../common/Toaster/Toaster";
import { LEADS_ROUTES, leadDetailPath } from "../../../features/leads/core/constants/routes";
import UserProfileView from "./UserProfileView";

const ALERT_MS = 3000;
const USERS_LIST_PATH = "/user";
const BACK_LINK = "inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-orange-primary";

/** React Router gives the first entry of a tab the key "default": there is no in-app screen to go back to. */
const hasInAppHistory = (location) => location.key !== "default";

/**
 * Back goes to the screen the profile was opened from (lead, Leads list, chat, Users…).
 * With no in-app history (a pasted or refreshed URL) it falls back to the lead
 * it was opened from, otherwise the Users list.
 * @param {{ location: { key: string, state?: { fromLead?: { userId: string, name?: string } } | null } }} props
 */
function BackNav({ location }) {
  const navigate = useNavigate();
  const fromLead = location.state?.fromLead;
  if (hasInAppHistory(location)) {
    return (
      <nav aria-label="Back" className="mb-3">
        <button type="button" className={BACK_LINK} onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {fromLead?.name ? `Back to ${fromLead.name}'s lead` : "Back"}
        </button>
      </nav>
    );
  }
  if (fromLead?.userId) {
    return (
      <nav aria-label="Back" className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link to={leadDetailPath(fromLead.userId)} className={BACK_LINK}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {fromLead.name ? `Back to ${fromLead.name}'s lead` : "Back to lead"}
        </Link>
        <span aria-hidden="true" className="text-gray-300">
          |
        </span>
        <Link to={LEADS_ROUTES.LIST} className={BACK_LINK}>
          Leads
        </Link>
      </nav>
    );
  }
  return (
    <nav aria-label="Back" className="mb-3">
      <Link to={USERS_LIST_PATH} className={BACK_LINK}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Users
      </Link>
    </nav>
  );
}

/** Users list → client profile route (/dashboard/:id). */
const UserInfoDashboard = () => {
  const { id } = useParams();
  const location = useLocation();
  const [alertMessage, setAlertMessage] = useState("");

  const showAlert = (message) => {
    setAlertMessage(message);
    setTimeout(() => setAlertMessage(""), ALERT_MS);
  };

  return (
    <div className="flex-1 flex flex-col items-center">
      {alertMessage && <Toaster message={alertMessage} type="success" />}
      <div className="w-full px-2 py-4 overflow-auto">
        <BackNav location={location} />
        <UserProfileView profileId={id} defaultTab="Nutrition" showAlert={showAlert} />
      </div>
    </div>
  );
};

export default UserInfoDashboard;
